import { type MenuProps } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { type ReactNode } from "react";

dayjs.extend(isoWeek);

export type CalendarView = "month" | "week" | "agenda";

export type DayMenuFactory = (day: Dayjs) => NonNullable<MenuProps["items"]>;

export interface RenderEventOptions {
  draggable: boolean;
  // Ten sam chip bywa zamontowany dwa razy (komórka dnia i dymek „+N więcej"). dnd-kit wymaga
  // unikalnego id, inaczej przeciąganie z komórki brało geometrię ukrytej kopii z dymka.
  dragIdScope?: string;
}

/** Co widoki kalendarza dostają od kalendarza konkretnej funkcji (klientka, wyzwanie). */
export interface CalendarEventsProps<T> {
  eventsByDay: Map<string, T[]>;
  getEventKey: (event: T, index: number) => string;
  renderEvent: (event: T, options: RenderEventOptions) => ReactNode;
  dayMenu?: DayMenuFactory;
  /** Klik w pustą część dnia (poza chipem). */
  onDayClick?: (day: Dayjs) => void;
  /** Dzień poza zakresem (np. poza terminem wyzwania) - przyciemniony, ale dalej działa. */
  isDayDimmed?: (day: Dayjs) => boolean;
}

export const WEEKDAY_LABELS = ["pon", "wt", "śr", "czw", "pt", "sob", "nd"];

const dayMonthFormat = new Intl.DateTimeFormat("pl-PL", { day: "numeric", month: "long" });
const monthYearFormat = new Intl.DateTimeFormat("pl-PL", { month: "long", year: "numeric" });
const longDayFormat = new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long" });

export const toDayKey = (date: Date | Dayjs | string) => dayjs(date).format("YYYY-MM-DD");

export const formatLongDay = (day: Dayjs) => longDayFormat.format(day.toDate());

export const getViewTitle = (view: CalendarView, date: Dayjs) => {
  if (view !== "week") {
    return monthYearFormat.format(date.toDate());
  }

  const start = date.startOf("isoWeek");
  const end = date.endOf("isoWeek");
  const from = start.month() === end.month() ? String(start.date()) : dayMonthFormat.format(start.toDate());

  return `${from} - ${dayMonthFormat.format(end.toDate())} ${end.year()}`;
};

export const getVisibleDays = (view: CalendarView, date: Dayjs): Dayjs[] => {
  const start = view === "week" ? date.startOf("isoWeek") : date.startOf("month").startOf("isoWeek");
  const end = view === "week" ? date.endOf("isoWeek") : date.endOf("month").endOf("isoWeek");
  const days: Dayjs[] = [];

  for (let day = start; !day.isAfter(end, "day"); day = day.add(1, "day")) {
    days.push(day);
  }

  return days;
};

export const shiftViewDate = (view: CalendarView, date: Date, direction: 1 | -1) =>
  dayjs(date)
    .add(direction, view === "week" ? "week" : "month")
    .toDate();

export const groupByDay = <T>(events: T[], getDate: (event: T) => Date | string) => {
  const byDay = new Map<string, T[]>();

  for (const event of events) {
    const key = toDayKey(getDate(event));
    const dayEvents = byDay.get(key);
    if (dayEvents) {
      dayEvents.push(event);
    } else {
      byDay.set(key, [event]);
    }
  }

  return byDay;
};

/** Przesuwa datę na wskazany dzień o pełne dni, więc godzina zostaje ta sama. */
export const moveToDay = (date: Date | string, targetDayKey: string) => {
  const from = dayjs(date);
  return from.add(dayjs(targetDayKey).diff(from.startOf("day"), "day"), "day");
};
